import { useEffect, type ReactNode } from "react";
import { setAuthBridge } from "@/shared/api/client";
import { useAuthStore, getAccessToken, getRefreshToken } from "@/features/auth/store";
import { refreshTokens } from "@/features/auth/api";
import { STORAGE_KEYS } from "@/shared/lib/constants";
import { createLogger } from "@/shared/lib/logger";

const log = createLogger("AUTH_PROVIDER");

export function AuthProvider({ children }: { children: ReactNode }) {
  const setTokens = useAuthStore((s) => s.setTokens);
  const setHydrated = useAuthStore((s) => s.setHydrated);
  const logout = useAuthStore((s) => s.logout);

  useEffect(() => {
    // Wire auth store ↔ API interceptors (single bridge for all requests via Kong)
    setAuthBridge({
      getAccessToken,
      getRefreshToken,
      onTokensRefreshed: (access, refresh) => setTokens(access, refresh),
      onAuthFailed: () => {
        log.warn("Auth failed — logging out");
        logout();
      },
    });

    log.debug("Auth bridge initialized");

    // Hydrate: try silent refresh from stored refresh token
    const storedRefresh = sessionStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
    if (storedRefresh) {
      log.info("Found stored refresh token, attempting silent refresh");
      refreshTokens(storedRefresh)
        .then((res) => {
          setTokens(res.auth.token, res.auth.refresh_token);
          log.info("Silent refresh successful");
        })
        .catch(() => {
          log.warn("Silent refresh failed, clearing stored token");
          sessionStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
        })
        .finally(() => setHydrated());
    } else {
      log.debug("No stored refresh token, marking hydrated");
      setHydrated();
    }
  }, [setTokens, setHydrated, logout]);

  return <>{children}</>;
}
